import { Table, Button, Flex, Modal, Typography, Empty, Dropdown, Tag, Tooltip } from 'antd';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import globalNotification from '../../common/hooks/globalNotification';
import dayjs from 'dayjs';
import lodash from 'lodash';
import PageContainer from '../../common/components/PageContainer';
import SearchInput from '../../common/components/GlobalSearch/SearchInput';
import { CustomPagination } from '../../common/components/pagination/Pagination';
import { SearchIcon, SorterIcon, FilterIcon, ClearInputIcon } from '../../common/components/icons/';
import TableFilterDropdown from '../../common/components/TableFilterDropdown';
import { useReadOnly } from '../../hooks/useReadOnly';
import RenderFilter from '../../components/leads/renderFilter';
import SideDrawer from '../../common/components/sideDrawer/SideDrawer';
import FiltersHeading from '../../common/components/FiltersHeading';
import TableHeadingButtons from '../../common/components/TableHeadingButtons';
import AddButton from '../../common/components/AddButton';
import MobileFiltersDrawer from '../../components/leads/mobileFiltersDrawer';
import { useMediaQuery } from '@uidotdev/usehooks';
import { notifications } from '../../common/constants/notifications';
import { notificationType } from '../../common/constants/notificationType';
import { jobStatusNames } from '../../common/constants/jobStatusNames';
import { getReportFilterOptions, withReportColumns } from './reportColumns';
// #region Utils and Services imports
import classNames from 'classnames';
import { useMedia } from '../../hooks/useMediaQuery';
import capitalize from '../../utils/capitalize';
import 'dayjs/locale/en';
import 'dayjs/locale/uk';
import { datesFormat } from '../../common/constants/datesFormat';
import { motion } from 'framer-motion';
import classes from './Jobs.module.scss';
import { SyncOutlined } from '@ant-design/icons';

// #endregion

// #region Styles and Theming imports
import {
    tagStyle,
    primitiveColors,
    textColor,
    fontSize,
    textStandard,
    pageHeading,
    spacing,
    buttonStyle,
    textSmall,
    borderColor,
    radius,
} from '../../theming';
import { useTranslation } from 'react-i18next';
import icon from '../../common/styles/Icon.module.scss';
import './Jobs.scss';
import { JobForm } from '../../components/jobs/jobForm';
import { useJobService } from '../../services/jobService';
import { useAuth } from '../../context/AuthContext';
import { checkForSuperAdmin } from '../../utils/checkRole';
import { sharedReportUrl } from '../../utils/sharedReportUrl';
import { withOrderedByColumn } from './orderedByColumn';
import { withDealerColumn } from './dealerColumn';
import { getStatusChangeTargets } from './statusChangeTargets';
import { canChangeStatus, statusChangeErrorCode } from '../../components/jobs/statusTransitions';
import { JobPreview } from '../../components/jobs/JobPreview';
import { getReportStatus } from '../../components/jobs/parserState';
import { getReportReadiness, needsReadyReport, reportNotReadyReason } from '../../components/jobs/reportReadiness';
import JobStatusControl from '../../common/components/JobsStatusSegmentController';
import { validateVinCode } from '../../utils/validateVinCode';
import { truncate } from './../../utils/truncate';
// #endregion

// why a status in a row's menu can't be picked: not a move the map allows (SC-QA-127), or the report isn't ready
// (SC-QA-53); null when it can
const statusMenuTooltip = ({ notAllowed, notReady, readiness, t }) => {
    if (notAllowed) return t('jobs:statusErrors.STATUS_TRANSITION_NOT_ALLOWED.title');
    if (notReady) return t(`jobs:reportReadiness.${readiness}`);
    return null;
};

function Jobs() {
    // read-only impersonation (#790): status changes are not offered
    const readOnly = useReadOnly();
    const { t, i18n } = useTranslation([
        'jobs',
        'general',
        'confirmModal',
        'notification',
        'apiResponses',
        'jobPreview',
        'confirmModal',
    ]);
    const currentLanguage = i18n.language;
    const { isMediumDevice } = useMedia();
    const { getJobs, getJobByUuid, createJob, updateJob, deleteJob, getStatuses, changeJobsStatus, regenerateReport } =
        useJobService();
    const { currentUser } = useAuth();
    const [urlSearchParams] = useSearchParams();

    const mobileDevicesBreakpoint = useMediaQuery('only screen and (max-width : 840px)');

    // #region States and Constants
    // Data:
    const [jobs, setJobs] = useState([]);
    const [viewJob, setViewJob] = useState(null);
    const [jobsCount, setJobsCount] = useState(0);
    const [statuses, setStatuses] = useState([]);

    const [loading, setLoading] = useState({
        tableLoading: false,
        addLoading: false,
        editLoading: false,
        deleteLoading: false,
        archiveLoading: false,
    });
    // Drawer states:
    const [open, setOpen] = useState(false);
    const [openMobileFilters, setOpenMobileFilters] = useState(false);
    const [formMode, setFormMode] = useState('add');
    const [jobToEdit, setJobToEdit] = useState(null);
    // filtration, sorters, search states:
    const [filteredInfo, setFilteredInfo] = useState({});
    const [selectedFilterValue, setSelectedFilterValue] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [searchParams, setSearchParams] = useState({
        pageSize: 10,
        currentPage: 1,
        isDeleted: false,
    });
    const [searchText, setSearchText] = useState('');
    // Table control states:
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({
        states: [],
        statuses: [],
        businesses: [],
    });
    const [isHovered, setIsHovered] = useState(null);
    const [selectedTab, setSelectedTab] = useState(t('general:statuses.active'));
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        const doneStatusId = statuses.find(s => s.name === jobStatusNames.Done)?.id;

        if (searchParams.status?.includes(doneStatusId)) {
            setSelectedTab(t('general:statuses.done'));
        } else {
            setSelectedTab(t('general:statuses.active'));
        }
    }, [i18n.language]);

    const filterOptions = {
        ...(checkForSuperAdmin(currentUser) ? getReportFilterOptions(t) : {}),
        status: statuses
            .filter(status => status.name !== jobStatusNames.Done)
            .map(status => ({
                text: t(`jobs:statuses.${status.name}`),
                value: status.id,
            })),
    };

    const filtersRenderCondition =
        Object.keys(filteredInfo).length > 0 &&
        Object.values(filteredInfo).some(value => (Array.isArray(value) ? value.length > 0 : value !== null));

    const [isClickFromView, setIsClickFromView] = useState(false);

    useEffect(() => {
        setSelectedFilterValue(filteredInfo);
    }, [filteredInfo]); // this is used to update the selectedValue state when filteredInfo changes. This was made due to behavior of antd select component
    // #endregion

    // #region Fetch Jobs Data
    const fetchData = async () => {
        setLoading(prevLoading => ({
            ...prevLoading,
            tableLoading: true,
        }));
        try {
            const statusesData = await getStatuses();
            setStatuses(statusesData.data.statuses.sort((a, b) => a.statusOrder - b.statusOrder));

            const params = { ...searchParams, search: searchText };

            // Filter params
            params.state = filteredInfo.state || undefined;
            params.reportType = filteredInfo.reportType?.length ? filteredInfo.reportType : undefined;
            params.internalDb = filteredInfo.internalDb?.length ? filteredInfo.internalDb : undefined;
            params.status =
                filteredInfo.status ||
                params.status ||
                statusesData.data.statuses.filter(s => s.name !== jobStatusNames.Done)?.map(s => s.id) ||
                [];

            // Sorting params
            switch (sortedInfo.column?.dataIndex) {
                case undefined:
                    params.sortField = 'createdAt';
                    break;
                default:
                    params.sortField = sortedInfo.column?.dataIndex;
                    break;
            }
            params.sortJob = sortedInfo.order === 'ascend' ? 'ASC' : 'DESC';

            const { data } = await getJobs(params);
            const { count, jobs } = data;

            setJobsCount(count);

            setJobs(
                jobs.map(job => ({
                    ...job,
                    key: job.id,
                    createdAt: capitalize(
                        dayjs(job.createdAt)
                            .locale(currentLanguage)
                            .format(
                                currentLanguage === 'uk' ? datesFormat.uk.longWithTime : datesFormat.en.shortWithTime
                            ),
                        currentLanguage === 'uk' ? 3 : 0
                    ),
                    updatedAt: capitalize(
                        dayjs(job.updatedAt)
                            .locale(currentLanguage)
                            .format(
                                currentLanguage === 'uk' ? datesFormat.uk.longWithTime : datesFormat.en.shortWithTime
                            ),
                        currentLanguage === 'uk' ? 3 : 0
                    ),
                    sharedLink: job.report?.sharedLink,
                }))
            );
        } finally {
            setLoading(prevLoading => ({
                ...prevLoading,
                tableLoading: false,
            }));
        }
    };

    useEffect(() => {
        fetchData();
    }, [searchParams, filteredInfo, i18n.language]);
    // #endregion

    // #region UUID from URL
    const getOneJob = async uuid => {
        try {
            const job = await getJobByUuid(uuid);
            setViewJob(job);
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.FETCH,
                plural: false,
            });
        }
    };

    useEffect(() => {
        const uuidFromUrl = urlSearchParams.get('u');
        if (uuidFromUrl && checkForSuperAdmin(currentUser)) {
            getOneJob(uuidFromUrl);
        }
    }, []);

    useEffect(() => {
        if (viewJob) {
            handleOpenPreviewDrawer(viewJob);
        }
    }, [viewJob]);
    // #endregion

    const handleUnsavedChangesUpdate = hasChanges => {
        setHasUnsavedChanges(hasChanges);
    };

    // #region Handlers
    const validateAndNotify = (values, loadingType) => {
        if (!values.vin || !values.model) {
            globalNotification.open({
                type: notificationType.ERROR,
                message: t('jobs:notifications.requiredFields.title'),
                description: t('jobs:notifications.requiredFields.description'),
            });

            setLoading(prevLoading => ({
                ...prevLoading,
                [loadingType]: false,
            }));

            return false;
        }

        const vinValidation = validateVinCode(values.vin, t);

        if (!vinValidation.result) {
            globalNotification.open({
                type: notificationType.ERROR,
                message: vinValidation.title,
                description: vinValidation.description,
            });

            setLoading(prevLoading => ({
                ...prevLoading,
                [loadingType]: false,
            }));

            return false;
        }

        return true;
    };

    const handleAddJob = async (values, formReset) => {
        setLoading(prevLoading => ({
            ...prevLoading,
            addLoading: true,
        }));

        try {
            if (!validateAndNotify(values, 'addLoading')) {
                return;
            }

            await createJob(values);

            globalNotification.request({
                type: notifications.type.SUCCESS,
                resource: notifications.resource.JOBS,
                action: notifications.action.ADD,
                plural: false,
            });

            setTimeout(() => {
                formReset();
            }, 1000);
            fetchData();
            closeDrawer();
        } catch (error) {
            const errCode = error.response?.data?.code;
            globalNotification.open({
                type: notificationType.ERROR,
                message: errCode ? t(`apiResponses:${errCode}.message`) : error.response?.data?.message,
                description: errCode ? t(`apiResponses:${errCode}.description`) : error.response?.data?.description,
            });
        } finally {
            setLoading(prevLoading => ({
                ...prevLoading,
                addLoading: false,
            }));
        }
    };

    const handleRowClick = lodash.debounce(record => {
        handleOpenPreviewDrawer(record);
    }, 300);

    const handleEditJob = async (id, values) => {
        setLoading(prevLoading => ({
            ...prevLoading,
            editLoading: true,
        }));

        if (!validateAndNotify(values, 'editLoading')) {
            return;
        }

        try {
            const updatedJob = await updateJob(id, values);
            globalNotification.request({
                type: notifications.type.SUCCESS,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
                plural: false,
            });
            fetchData();
            closeDrawer();

            return { ...updatedJob };
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
                plural: false,
            });
            throw error;
        } finally {
            setLoading(prevLoading => ({
                ...prevLoading,
                editLoading: false,
            }));
        }
    };

    // #region Delete Job
    const onClickDeleteJobs = async ids => {
        Modal.confirm({
            title: `${t('confirmModal:actions.delete')} ${ids.length > 1 ? t('jobs:confirm.plural') : t('jobs:confirm.singular')}?`,
            icon: null,
            width: 'fit-content',
            styles: { body: { padding: spacing[4] } },
            okText: `${t('confirmModal:buttons.yes')}`,
            cancelText: `${t('confirmModal:buttons.no')}`,
            okButtonProps: { danger: true },
            onOk: async () => {
                await handleDeleteJobs(ids);
            },
        });
    };

    const handleDeleteJobs = async ids => {
        setLoading(prevLoading => ({
            ...prevLoading,
            deleteLoading: true,
        }));

        try {
            await deleteJob(ids);
            globalNotification.request({
                type: notifications.type.SUCCESS,
                resource: notifications.resource.JOBS,
                action: notifications.action.DELETE,
                plural: ids.length > 1,
            });
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.DELETE,
                plural: ids.length > 1,
            });
        } finally {
            fetchData();
            setLoading(prevLoading => ({
                ...prevLoading,
                deleteLoading: false,
            }));
            setSelectedJobs([]);
        }
    };
    // #endregion

    // #region CRUD Drawer
    const openDrawer = () => {
        setOpen(true);
    };
    const closeDrawer = () => {
        setOpen(false);
        setHasUnsavedChanges(false);

        setTimeout(() => {
            setFormMode('');
        }, 500);
    };

    const openAddDrawer = () => {
        setIsClickFromView(false);
        setFormMode('add');
        openDrawer();
    };

    const openEditDrawer = (job, isClickFromView = false) => {
        if (!isClickFromView) {
            setIsClickFromView(false);
        }

        setJobToEdit(job);
        setFormMode('edit');
        openDrawer();
    };

    const openEditMode = job => {
        closeDrawer();
        setIsClickFromView(true);

        setTimeout(() => {
            openEditDrawer(job, true);
        }, 500);
    };

    const handleOpenPreviewDrawer = job => {
        setFormMode('preview');
        setJobToEdit(job);
        openDrawer();

        const url = new URL(window.location.href);
        url.searchParams.delete('u');
        window.history.replaceState({}, '', url);
    };
    // #endregion

    // #region Search
    const handleSearchJob = lodash.debounce(searchText => {
        setSearchText(searchText);
        setSearchParams(prevValue => ({
            ...prevValue,
            currentPage: 1,
        }));
    }, 200);

    const handleResetSearch = () => {
        setSearchText('');
        setSearchParams(prevValue => ({
            ...prevValue,
            currentPage: 1,
        }));
        urlSearchParams.delete('vin');
    };
    // #endregion

    // #region Status change
    const handlePageChange = page => {
        setSearchParams(prevValue => ({
            ...prevValue,
            currentPage: page,
        }));
    };

    // Returns true only when the change was saved, so Order Preview never reports a status that wasn't
    // a refused Verification or Done says why (SC-QA-53); anything else keeps the generic toast
    const notifyStatusChangeError = error => {
        // SC-QA-127: another dealer took the job first, or a move the map refuses: say which, and show the list as it is
        const code = statusChangeErrorCode(error);
        if (code) {
            globalNotification.open({
                type: notifications.type.ERROR,
                message: t(`jobs:statusErrors.${code}.title`),
                description: t(`jobs:statusErrors.${code}.description`),
            });
            fetchData();
            return;
        }

        const reason = reportNotReadyReason(error);

        if (reason) {
            globalNotification.open({
                type: notifications.type.ERROR,
                message: t('jobs:reportReadiness.title'),
                description: t(`jobs:reportReadiness.${reason}`),
            });
            return;
        }

        globalNotification.request({
            type: notifications.type.ERROR,
            resource: notifications.resource.JOBS,
            action: notifications.action.UPDATE,
        });
    };

    const handleStatusChange = async (jobIds, updatedStatus, isPreviewClick = false) => {
        const status = statuses.find(status => status.name === updatedStatus.name);
        const { selectedJobsData, jobsToUpdate } = getStatusChangeTargets({
            jobIds,
            status,
            isPreviewClick,
            selectedJobs,
            jobs,
            isSuperAdmin: checkForSuperAdmin(currentUser),
        });

        if (!jobsToUpdate.length) {
            return false;
        }

        if (status.name === jobStatusNames.Rejected && !isPreviewClick) {
            Modal.confirm({
                title: t('jobPreview:confirmReject.title'),
                content: t('jobPreview:confirmReject.content'),
                icon: null,
                styles: { body: { padding: spacing[4] } },
                width: 'fit-content',
                okButtonProps: {
                    danger: true,
                },
                centered: true,
                okText: t('confirmModal:buttons.yes'),
                cancelText: t('confirmModal:buttons.cancel'),
                onOk: async () => {
                    const jobIdsToUpdate = jobsToUpdate.map(job => job.id);
                    try {
                        await changeJobsStatus(jobIdsToUpdate, status.id);
                    } catch (error) {
                        notifyStatusChangeError(error);
                        return;
                    }

                    if (jobIdsToUpdate.length < selectedJobsData.length) {
                        globalNotification.open({
                            type: notifications.type.WARNING,
                            message: t('jobPreview:warnings.message'),
                            description: t('jobs:notifications.partialUpdate.description', {
                                updated: jobIdsToUpdate.length,
                                total: selectedJobsData.length,
                            }),
                            placement: 'top',
                        });
                    } else {
                        globalNotification.request({
                            type: notifications.type.SUCCESS,
                            resource: notifications.resource.JOBS,
                            action: notifications.action.UPDATE,
                            plural: true,
                        });
                    }

                    fetchData();
                    setSelectedJobs([]);
                },
            });

            // saved later, if the user confirms
            return false;
        }

        try {
            const jobIdsToUpdate = jobsToUpdate.map(job => job.id);
            await changeJobsStatus(jobIdsToUpdate, status.id);

            if (jobIdsToUpdate.length < selectedJobsData.length) {
                globalNotification.open({
                    type: notifications.type.WARNING,
                    message: t('jobPreview:warnings.message'),
                    description: t('jobs:notifications.partialUpdate.description', {
                        updated: jobIdsToUpdate.length,
                        total: selectedJobsData.length,
                    }),
                    placement: 'top',
                });
            } else if (!isPreviewClick) {
                globalNotification.request({
                    type: notifications.type.SUCCESS,
                    resource: notifications.resource.JOBS,
                    action: notifications.action.UPDATE,
                    plural: true,
                });
            }

            fetchData();

            if (status.name === jobStatusNames.Done && isPreviewClick) {
                closeDrawer();
            }

            return true;
        } catch (error) {
            notifyStatusChangeError(error);

            return false;
        } finally {
            setSelectedJobs([]);
        }
    };

    const onSelectChange = newSelectedRowKeys => {
        setSelectedJobs(newSelectedRowKeys);
    };

    const rowSelection = {
        fixed: true,
        selectedRowKeys: selectedJobs.map(job => job.id),
        onChange: (_, selectedRows) => {
            onSelectChange(
                selectedRows.map(row => {
                    return {
                        id: row.id,
                        isDeleted: row.isDeleted,
                        status: row.status,
                    };
                })
            );
        },
        onSelect: (record, selected) => {
            if (selected) {
                setSelectedJobs(prevSelected => [
                    ...prevSelected,
                    {
                        id: record.id,
                        isDeleted: record.isDeleted,
                        status: record.status,
                    },
                ]);
            } else {
                setSelectedJobs(prevSelected => prevSelected.filter(item => item.id !== record.key));
            }
        },
        onSelectAll: (selected, _, changeRows) => {
            const selectedKeys = changeRows.map(row => row.key);
            if (selected) {
                setSelectedJobs(prevSelected => [...prevSelected, ...selectedKeys]);
            } else {
                setSelectedJobs(prevSelected => prevSelected.filter(id => !selectedKeys.includes(id)));
            }
        },

        getCheckboxProps: record => ({
            name: record.name,
        }),
    };

    const statusHandler = value => {
        switch (value) {
            case t('general:statuses.done'):
                setSearchParams(prevValue => ({
                    ...prevValue,
                    currentPage: 1,
                    status: [statuses.find(s => s.name === jobStatusNames.Done)?.id] || [],
                }));
                setSelectedTab(t('general:statuses.done'));

                setFilteredInfo(prevFilteredInfo => ({
                    ...prevFilteredInfo,
                    status: null,
                }));
                break;

            case t('general:statuses.active'):
                setSearchParams(prevValue => ({
                    ...prevValue,
                    currentPage: 1,
                    status: statuses.filter(s => s.name !== jobStatusNames.Done)?.map(s => s.id) || [],
                }));
                setSelectedTab(t('general:statuses.active'));

                setFilteredInfo(prevFilteredInfo => ({
                    ...prevFilteredInfo,
                    status: null,
                }));
                break;
            default:
                break;
        }
        setSelectedJobs([]);
    };
    // #endregion
    // #endregion

    // #region Table Config
    const onTableParamsChange = (pagination, filters, sorter) => {
        // If we're on the Done tab, don't apply status filters
        if (selectedTab === t('general:statuses.done') && filters.status) {
            filters = { ...filters, status: null };
        }

        setSearchParams(prevValue => ({
            ...prevValue,
            currentPage: pagination.current || 1,
            pageSize: pagination.pageSize || 10,
        }));
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    const handleRegenerateReport = async jobUuid => {
        try {
            await regenerateReport(jobUuid);
            globalNotification.request({
                type: notifications.type.SUCCESS,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
            });

            fetchData();
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
            });
        }
    };

    const columns = [
        {
            title: t('fields.vin'),
            dataIndex: 'vin',
            key: 'vin',
            fixed: mobileDevicesBreakpoint ? false : 'left',
            width: mobileDevicesBreakpoint ? 190 : 280,
            render: name => (
                <Typography.Text style={{ ...(mobileDevicesBreakpoint ? textSmall : textStandard), cursor: 'pointer' }}>
                    {name}
                </Typography.Text>
            ),
        },
        {
            title: t('fields.brand'),
            dataIndex: 'brand',
            key: 'brand',
            width: 140,
            sorter: () => {}, // empty because we are sorting on backend
            defaultSortOrder: 'descend',
            sorterOrder: sortedInfo.columnKey === 'brand' ? sortedInfo.order : null,
            render: brand => (
                <Typography.Text style={{ ...(mobileDevicesBreakpoint ? textSmall : textStandard) }}>
                    {brand}
                </Typography.Text>
            ),
        },
        {
            title: t('fields.model'),
            dataIndex: 'model',
            key: 'model',
            width: mobileDevicesBreakpoint ? 100 : 172,
            sorter: () => {}, // empty because we are sorting on backend
            sorterOrder: sortedInfo.columnKey === 'status' ? sortedInfo.order : null,
            render: model => (
                <Tooltip title={model} onClick={e => e.stopPropagation()}>
                    <Typography.Text style={{ ...(mobileDevicesBreakpoint ? textSmall : textStandard) }}>
                        {truncate(model, mobileDevicesBreakpoint ? 10 : 25)}
                    </Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: t('fields.status'),
            dataIndex: 'status',
            key: 'status',
            width: mobileDevicesBreakpoint ? 100 : 172,
            filters:
                !mobileDevicesBreakpoint && selectedTab !== t('general:statuses.done')
                    ? [
                          ...statuses
                              .filter(status => status.name !== jobStatusNames.Done)
                              .map(status => ({
                                  text: t(`jobs:statuses.${status.name}`),
                                  value: status.id,
                              })),
                      ]
                    : null,
            onFilter:
                selectedTab !== t('general:statuses.done')
                    ? (value, record) => {
                          return record.status?.id === value;
                      }
                    : null,
            filteredValue:
                selectedTab !== t('general:statuses.done') && filteredInfo.status ? filteredInfo.status : null,
            sorter: () => {}, // empty because we are sorting on backend
            sorterOrder: sortedInfo.columnKey === 'status' ? sortedInfo.order : null,
            render: (status, record) => {
                // SC-QA-53: Verification and Done wait for the report (file uploaded and parse finished)
                const readiness = getReportReadiness(record);
                return (
                    <Dropdown
                        disabled={readOnly}
                        placement="bottomLeft"
                        menu={{
                            items: statuses.map(status => {
                                const notReady =
                                    needsReadyReport(status.name) &&
                                    readiness !== 'ready' &&
                                    record.status?.name !== status.name;

                                // SC-QA-127: only the moves the status map allows this user from the row's status
                                const notAllowed =
                                    record.status?.name !== status.name &&
                                    !canChangeStatus(record.status?.name, status.name, checkForSuperAdmin(currentUser));

                                return {
                                    disabled: notAllowed || notReady,
                                    key: status.id,
                                    label: (
                                        <Tooltip title={statusMenuTooltip({ notAllowed, notReady, readiness, t })}>
                                            {t(`jobs:statuses.${status.name}`)}
                                        </Tooltip>
                                    ),
                                    onClick: ({ domEvent }) => {
                                        domEvent.stopPropagation();
                                        handleStatusChange([record.id], status);
                                    },
                                };
                            }),
                        }}
                        overlayClassName={classes['actions-menu']}
                        trigger="click"
                    >
                        <motion.div
                            onClick={e => e.stopPropagation()}
                            initial="hidden"
                            animate={isHovered === record.id ? 'visible' : 'hidden'}
                            onHoverStart={() => setIsHovered(record.id)}
                            onHoverEnd={() => setIsHovered(null)}
                            style={{
                                position: 'relative',
                                cursor: 'pointer',
                                width: 'fit-content',
                            }}
                        >
                            <motion.div
                                variants={{
                                    hidden: { width: 'auto' },
                                    visible: { width: 'auto', paddingRight: '28px' },
                                }}
                                transition={{ duration: 0.2 }}
                            >
                                <Tag
                                    style={{
                                        ...tagStyle,
                                        color: status?.primaryColor,
                                        backgroundColor: status?.secondaryColor,
                                        height: '28px',
                                        fontSize: mobileDevicesBreakpoint ? fontSize.xxs : 'inherit',
                                        zIndex: 1,
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    bordered={false}
                                >
                                    <span>{t(`jobs:statuses.${status?.name}`)}</span>
                                    <motion.div
                                        variants={{
                                            hidden: { opacity: 0, width: 0, marginLeft: 0 },
                                            visible: { opacity: 1, width: 'auto', marginLeft: spacing[1] },
                                        }}
                                        transition={{ duration: 0.2 }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginTop: spacing[0.5],
                                        }}
                                    >
                                        <i
                                            className="fi fi-rr-edit"
                                            style={{
                                                fontSize: fontSize.xs,
                                                color: status?.primaryColor,
                                            }}
                                        />
                                    </motion.div>
                                </Tag>
                            </motion.div>
                        </motion.div>
                    </Dropdown>
                );
            },
            filterDropdown: ({ filters, close }) =>
                !mobileDevicesBreakpoint && selectedTab !== t('general:statuses.done') ? (
                    <TableFilterDropdown
                        filters={filters || []}
                        close={close}
                        filterKey="status"
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                        setFilteredInfo={setFilteredInfo}
                        setSearchParams={setSearchParams}
                    />
                ) : null,
        },
        {
            title: t('fields.language'),
            dataIndex: 'language',
            key: 'language',
            width: mobileDevicesBreakpoint ? 80 : 110,
            render: source => (
                <Typography.Text style={{ ...(mobileDevicesBreakpoint ? textSmall : textStandard) }}>
                    {source || 'en'}
                </Typography.Text>
            ),
        },
        {
            title: t('fields.created'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            sorter: () => {}, // empty because we are sorting on backend
            defaultSortOrder: 'descend',
            sorterOrder: sortedInfo.columnKey === 'createdAt' ? sortedInfo.order : null,
            render: source => (
                <Typography.Text style={{ ...(mobileDevicesBreakpoint ? textSmall : textStandard) }}>
                    {source}
                </Typography.Text>
            ),
        },
        {
            title: t('fields.updated'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 170,
            sorter: () => {}, // empty because we are sorting on backend
            defaultSortOrder: 'descend',
            sorterOrder: sortedInfo.columnKey === 'updatedAt' ? sortedInfo.order : null,
            render: location => (
                <Typography.Text style={{ ...(mobileDevicesBreakpoint ? textSmall : textStandard) }}>
                    {location}
                </Typography.Text>
            ),
        },
    ];

    if (checkForSuperAdmin(currentUser)) {
        columns.push(
            {
                title: t('fields.isParserReportReady'),
                key: 'isParserReportReady',
                width: 80,
                align: 'center',
                render: (_, record) => {
                    const { flag, color } = getReportStatus(record);

                    return (
                        <Flex
                            justify="center"
                            align="center"
                            children={
                                <Tag
                                    icon={flag === 'pending' ? <SyncOutlined spin /> : null}
                                    color={color}
                                    style={{
                                        borderRadius: radius.sm,
                                        padding: `0 ${spacing[3]}`,
                                        textAlign: 'center',
                                        letterSpacing: '0.075em',
                                    }}
                                >
                                    {t(`jobErrors.flags.${flag}`).toUpperCase()}
                                </Tag>
                            }
                        />
                    );
                },
            },
            {
                title: '',
                dataIndex: 'sharedLink',
                key: 'sharedLink',
                width: 40,
                render: (value, record) => (
                    <Button
                        href={sharedReportUrl(record?.report?.sharedLink, i18n.resolvedLanguage)}
                        target="_blank"
                        aria-label="Preview Report"
                        disabled={!record?.report?.sharedLink}
                        style={{ cursor: 'pointer', width: '100%' }}
                        icon={<i className="fi fi-rr-eye" />}
                        onClick={e => {
                            e.stopPropagation();
                        }}
                    />
                ),
            },
            {
                title: '',
                dataIndex: 'regenerateReport',
                key: 'regenerateReport',
                width: 40,
                render: (value, record) => (
                    <Button
                        disabled={!record?.error}
                        aria-label="Regenerate Report"
                        icon={<i className="fi fi-rr-refresh" style={{ cursor: 'pointer', width: '100%' }} />}
                        onClick={e => {
                            e.stopPropagation();
                            handleRegenerateReport(record.uuid);
                        }}
                    />
                ),
            }
        );
    }

    const clearAll = () => {
        setFilteredInfo({});
        setSortedInfo({});
        setSelectedFilters({
            states: [],
            statuses: [],
            businesses: [],
        });
    };
    // #endregion

    return (
        <PageContainer
            style={{
                backgroundColor: mobileDevicesBreakpoint ? primitiveColors.gray25 : primitiveColors.gray0,
                padding: mobileDevicesBreakpoint ? spacing[1] : spacing[4],
            }}
        >
            <Flex vertical>
                <Flex
                    vertical={mobileDevicesBreakpoint}
                    gap={mobileDevicesBreakpoint ? spacing[1] : spacing[3]}
                    align="center"
                    justify="space-between"
                    style={{
                        marginBottom: mobileDevicesBreakpoint ? spacing[3] : spacing[4],
                    }}
                >
                    <Flex
                        gap={spacing[3]}
                        align="center"
                        justify={mobileDevicesBreakpoint ? 'space-between' : 'start'}
                        style={{
                            width: mobileDevicesBreakpoint && '100%',
                            maxHeight: mobileDevicesBreakpoint && 60,
                        }}
                    >
                        <h1 style={{ ...pageHeading, fontSize: mobileDevicesBreakpoint && fontSize.H4, margin: 0 }}>
                            {t('plural')}
                        </h1>
                        <JobStatusControl onChange={statusHandler} value={selectedTab} />
                    </Flex>
                    <Flex
                        gap={spacing[3]}
                        align="center"
                        justify={mobileDevicesBreakpoint ? 'space-between' : 'end'}
                        style={{
                            width: mobileDevicesBreakpoint && '100%',
                            flexDirection: mobileDevicesBreakpoint ? 'row' : 'row-reverse',
                        }}
                    >
                        {currentUser.userRoleNames.includes('Super Admin') && (
                            <AddButton
                                size={isMediumDevice && mobileDevicesBreakpoint && 'default'}
                                style={{
                                    ...(mobileDevicesBreakpoint ? buttonStyle.small : buttonStyle.large),
                                    fontSize: mobileDevicesBreakpoint && fontSize.xs,
                                }}
                                iconSize={icon.sm}
                                text={t('actions.add')}
                                onClick={openAddDrawer}
                            />
                        )}
                        <Flex
                            gap={spacing[3]}
                            align="center"
                            justify={mobileDevicesBreakpoint ? 'space-between' : 'end'}
                            style={{ width: mobileDevicesBreakpoint && 300 }}
                        >
                            {mobileDevicesBreakpoint && selectedTab !== t('general:statuses.done') && (
                                <>
                                    <Button
                                        icon={<i className={`fi fi-rr-settings-sliders`} />}
                                        style={{
                                            ...buttonStyle.small,
                                            color: textColor.subtle,
                                            borderColor: textColor.subtle,
                                            backgroundColor: 'inherit',
                                            width: 'clamp(123px, 20vw, 140px)',
                                            fontSize: fontSize.sm,
                                        }}
                                        onClick={() => setOpenMobileFilters(true)}
                                    >
                                        {t('actions.filters')}
                                    </Button>
                                    <MobileFiltersDrawer
                                        open={openMobileFilters}
                                        closeDrawer={() => setOpenMobileFilters(false)}
                                        filteredInfo={filteredInfo}
                                        filterOptions={filterOptions}
                                        selectedValue={selectedFilterValue}
                                        setSelectedValue={setSelectedFilterValue}
                                        setSelectedFilters={setSelectedFilters}
                                        setFilteredInfo={setFilteredInfo}
                                        setSearchParams={setSearchParams}
                                    />
                                </>
                            )}
                            <SearchInput
                                placeholder={t('search')}
                                value={searchText}
                                style={{
                                    height: mobileDevicesBreakpoint && 36,
                                    borderColor: mobileDevicesBreakpoint && borderColor.secondaryAlt,
                                }}
                                onChange={e => {
                                    setSearchText(e.target.value.trim());
                                    if (e.target.value.trim() === '') {
                                        handleResetSearch();
                                    }
                                }}
                                onPressEnter={e => {
                                    e.preventDefault();
                                    handleSearchJob(searchText);
                                }}
                                prefix={<SearchIcon onClick={() => handleSearchJob(searchText)} />}
                                suffix={
                                    searchText && (
                                        <ClearInputIcon setSearchText={setSearchText} handleReset={handleResetSearch} />
                                    )
                                }
                            />
                        </Flex>
                    </Flex>
                    {selectedJobs.length > 0 && mobileDevicesBreakpoint && (
                        <Flex
                            gap={spacing[0.5]}
                            align="center"
                            justify="end"
                            style={{ width: mobileDevicesBreakpoint && '100%' }}
                        >
                            <TableHeadingButtons
                                items={selectedJobs}
                                handleDelete={onClickDeleteJobs}
                                isMobile
                                style={{
                                    ...buttonStyle.small,
                                }}
                            />
                        </Flex>
                    )}
                </Flex>
                {!mobileDevicesBreakpoint && (
                    <Flex
                        align="center"
                        justify={selectedJobs.length > 0 ? 'end' : 'space-between'}
                        style={{ marginBottom: spacing[1], height: 38 }}
                    >
                        {filtersRenderCondition && !selectedJobs.length && (
                            <Flex gap={spacing[2]} align="center">
                                <FiltersHeading />
                                {Object.keys(filteredInfo).map((key, index) => (
                                    <RenderFilter
                                        key={key}
                                        index={index}
                                        filteredKey={key}
                                        filteredInfo={filteredInfo}
                                        filterOptions={filterOptions}
                                        selectedValue={selectedFilterValue}
                                        setSelectedValue={setSelectedFilterValue}
                                        setSelectedFilters={setSelectedFilters}
                                        setFilteredInfo={setFilteredInfo}
                                    />
                                ))}
                                <Button
                                    type="link"
                                    onClick={() => {
                                        clearAll();
                                    }}
                                    size="small"
                                    style={{
                                        color: textColor.subtle,
                                    }}
                                >
                                    {t('actions.reset')}
                                </Button>
                            </Flex>
                        )}
                        {selectedJobs.length > 0 && (
                            <Flex
                                gap={spacing[0.5]}
                                align="center"
                                justify="end"
                                style={{ width: mobileDevicesBreakpoint && '100%' }}
                            >
                                <TableHeadingButtons
                                    items={selectedJobs}
                                    handleDelete={onClickDeleteJobs}
                                    isMobile
                                    style={{
                                        ...buttonStyle.small,
                                    }}
                                />
                            </Flex>
                        )}
                    </Flex>
                )}
                <SideDrawer
                    open={open}
                    closeDrawer={closeDrawer}
                    title={formMode === 'add' ? t('actions.add') : formMode === 'preview' ? '' : t('actions.edit')}
                    width={formMode === 'preview' && 700}
                    padding={formMode === 'preview' ? `${spacing[10]} ${spacing[6]}` : ''}
                    outsideClick={formMode === 'preview'}
                    hasUnsavedChanges={hasUnsavedChanges}
                    children={
                        formMode === 'preview' ? (
                            <JobPreview
                                job={jobToEdit}
                                openEditMode={openEditMode}
                                statuses={statuses}
                                handleStatus={handleStatusChange}
                                onHasUnsavedChanges={handleUnsavedChangesUpdate}
                                hasUnsavedChanges={hasUnsavedChanges}
                            />
                        ) : (
                            <JobForm
                                mode={formMode}
                                job={jobToEdit}
                                loading={formMode === 'add' ? loading.addLoading : loading.editLoading}
                                handleAdd={handleAddJob}
                                handleEdit={handleEditJob}
                                openPreviewDrawer={handleOpenPreviewDrawer}
                                isClickFromView={isClickFromView}
                                statuses={statuses}
                                onHasUnsavedChanges={handleUnsavedChangesUpdate}
                                hasUnsavedChanges={hasUnsavedChanges}
                            />
                        )
                    }
                />
            </Flex>

            <Table
                onRow={record => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' },
                })}
                columns={[
                    Table.SELECTION_COLUMN,
                    Table.EXPAND_COLUMN,
                    ...withReportColumns(
                        withDealerColumn(
                            withOrderedByColumn(columns, {
                                currentUser,
                                t,
                                textStyle: mobileDevicesBreakpoint ? textSmall : textStandard,
                            }),
                            { currentUser, t, textStyle: mobileDevicesBreakpoint ? textSmall : textStandard }
                        ),
                        {
                            currentUser,
                            t,
                            textStyle: mobileDevicesBreakpoint ? textSmall : textStandard,
                            filteredInfo,
                            showHeaderFilters: !mobileDevicesBreakpoint,
                        }
                    ).map(column => ({
                        ...column,
                        sortOrder: sortedInfo?.columnKey === column.dataIndex && sortedInfo?.order,
                        title: column.title.toUpperCase(),
                        filterIcon: filtered =>
                            !mobileDevicesBreakpoint &&
                            selectedTab !== t('general:statuses.done') &&
                            FilterIcon({ filtered }),
                        sortIcon: () => SorterIcon({ sortOrder: sortedInfo, column: column.dataIndex }),
                    })),
                ]}
                dataSource={jobs}
                onChange={onTableParamsChange}
                pagination={false}
                loading={loading.tableLoading}
                scroll={{
                    x: 'max-content',
                }}
                size="small"
                selection
                bordered={false}
                rowSelection={
                    checkForSuperAdmin(currentUser)
                        ? {
                              ...rowSelection,

                              type: 'checkbox',
                          }
                        : false
                }
                locale={{
                    triggerAsc: `${t('general:actions.simple.sort-asc')}`,
                    triggerDesc: t('general:actions.simple.sort-desc'),
                    cancelSort: t('general:actions.simple.cancel-sort'),
                    emptyText: <Empty description={t('general:no-data')} />,
                }}
                rowClassName={(record, index) =>
                    classNames({
                        'even-table-cell': index % 2 === 0,
                        'odd-table-cell': index % 2 !== 0,
                        'expanded-cell': expandedRowKeys.includes(record.key),
                    })
                }
                defaultSortOrder="descend"
                footer={() => (
                    <CustomPagination
                        currentPage={searchParams.currentPage}
                        handlePageChange={handlePageChange}
                        perPage={searchParams.pageSize}
                        handlePerPageChange={perPage => {
                            setSearchParams(prevValue => ({
                                ...prevValue,
                                pageSize: perPage,
                                currentPage: 1,
                            })); // reset page to 1 when changing perPage
                        }}
                        total={jobsCount}
                        title={t('total')}
                    />
                )}
            />
        </PageContainer>
    );
}

export default Jobs;
